// Lightweight, query-param-driven feature flags.
//
// A flag is switched on by visiting any URL with `?flag=<name>`. It's persisted
// to sessionStorage so it survives client-side navigation and external redirects
// within the same tab — e.g. the SSO IdP bouncing back to /auth/sso/callback,
// which doesn't carry the query string — but it resets when the tab/session ends,
// so flags default to off each session rather than sticking forever. Turn one off
// in the current session with `?flag=-<name>`. Multiple flags: `?flag=a,b` or
// repeated `?flag=a&flag=b`.

const STORAGE_KEY = 'ae_feature_flags';

function readPersisted(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function writePersisted(flags: Set<string>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...flags]));
  } catch {
    // Storage unavailable (private mode, quota) — flags just won't persist.
  }
}

// Apply the URL's `flag` tokens on top of a set: `name` enables, `-name` disables.
function applyTokens(flags: Set<string>, flagParam: string): Set<string> {
  const next = new Set(flags);
  for (const token of flagParam.split(',')) {
    const name = token.trim();
    if (!name) continue;
    if (name.startsWith('-')) next.delete(name.slice(1));
    else next.add(name);
  }
  return next;
}

// Pure read: persisted flags with the current URL's `flag` tokens applied.
export function resolveEnabledFlags(flagParam: string): Set<string> {
  return applyTokens(readPersisted(), flagParam);
}

// Persist the URL's `flag` tokens so the change sticks across navigation.
export function applyFlagParam(flagParam: string): void {
  writePersisted(applyTokens(readPersisted(), flagParam));
}
